// Sidebar, drawn by AppShell (MGMT-PANEL-BUILD-SHELL-01, direction A).
// Kept exactly from the previous Sidebar.js (7d83b73): the two groups and their order, the NEW badges,
// the Leads and Preferences counts (same two GETs, same numbers), the Pricing role gate, the City Manager
// rule for Overview, and Logout. New in PB1: the active item is tinted with a 3px accent bar in both
// groups, and matching ignores case so /BookingDashboard highlights.
// MGMT-PANEL-SHELL-REGRESSIONS-01 (PB1b) restores what PB1 dropped: the United wordmark image
// (unitedai.png), the same icon per item as 7d83b73 (same @mui/icons-material component, Whook keeps its
// emoji), and the ChevronLeft / ChevronRight collapse control at the top. The pre-redesign element list is
// asserted by Sidebar.inventory.test.js (MGMT-PANEL-KEEP-LIST-ASSERTED-01).
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Tooltip from '@mui/material/Tooltip';
import {
    Home as HomeIcon,
    CalendarToday as CalendarIcon,
    Group as GroupIcon,
    Sports as SportsIcon,
    LocationOn as LocationOnIcon,
    BarChart as BarChartIcon,
    Event as EventIcon,
    VideoLibrary as VideoLibraryIcon,
    PlaylistAddCheck as WaitlistIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ExitToApp as ExitToAppIcon,
    Store as StoreIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Business as BusinessIcon,
    PeopleAlt as PeopleAltIcon,
    PersonAdd as ApplicantsIcon,
    Work as WorkIcon,
    TrendingUp as TrendingUpIcon,
    Outbox as OutboxIcon,
    AutoAwesome as AutoAwesomeIcon,
    Inventory2 as Inventory2Icon,
    Science as ScienceIcon,
    Campaign as CampaignIcon,
    DescriptionOutlined as TranscriptIcon,
    AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import { useEmployee } from '../../contexts/EmployeeContext';
import { usePricingAccess } from '../../hooks/usePricingAccess';
import { FONT_MONO } from '../../theme/united';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 68;

// The wordmark the sidebar drew before the redesign (7d83b73 Sidebar.js:239). It is cream on a transparent
// ground, made for the old dark sidebar; there is no dark-ink file, so the light scheme draws the same image
// as a silhouette (CSS filter, no colour typed in) and the dark scheme draws it as it is.
export const LOGO_SRC = 'https://d3dbfxrvl5y81z.cloudfront.net/unitedai.png';

// Icons: the same component per item as 7d83b73 Sidebar.js:128-164 (Whook was, and is, the emoji).
export const ENTERPRISE_AI_ITEMS = [
    { to: '/genesis', label: 'Genesis', icon: AutoAwesomeIcon, badge: 'NEW' },
    { to: '/agent-builder', label: 'Agent Builder', icon: AccountTreeIcon, badge: 'NEW' },
    { to: '/transcripts', label: 'Transcripts', icon: TranscriptIcon, badge: 'NEW' },
    { to: '/outbound', label: 'GTM', icon: CampaignIcon, badge: 'NEW' },
    { to: '/inbound-leads', label: 'Leads', icon: MoveToInboxIcon, badge: 'NEW' },
    { to: '/memory', label: 'Memory', icon: Inventory2Icon },
    { to: '/eval', label: 'Eval Harness', icon: ScienceIcon },
    { to: '/enterprises', label: 'Enterprise', icon: BusinessCenterIcon },
    { to: '/money', label: 'Money', icon: AccountBalanceWalletIcon },
    { to: '/united-pricing', label: 'Pricing', icon: PriceChangeIcon, badge: 'NEW', requiresPricingRole: true },
];

// Workspace items in their existing order (the Enterprise item lives in Enterprise AI above).
export const WORKSPACE_ITEMS = [
    { to: '/whook', label: 'Whook', icon: '🧠' },
    { to: '/dashboard', label: 'Overview', icon: HomeIcon, hideIfCityManager: true },
    { to: '/BookingDashboard', label: 'Dashboard', icon: CalendarIcon },
    { to: '/leads', label: 'Leads', icon: PeopleAltIcon, countKey: 'leads' },
    { to: '/preferences', label: 'Preferences', icon: TrendingUpIcon, countKey: 'preferences' },
    { to: '/outreach', label: 'Outreach', icon: OutboxIcon },
    { to: '/waitlist', label: 'Waitlist', icon: WaitlistIcon },
    { to: '/athletes', label: 'Users', icon: GroupIcon },
    { to: '/coaches', label: 'Instructors', icon: SportsIcon },
    { to: '/sitemanagers', label: 'Site staff', icon: SupervisorAccountIcon },
    { to: '/couriers', label: 'Couriers', icon: LocalShippingIcon },
    { to: '/employees', label: 'Employees', icon: WorkIcon },
    { to: '/applicants', label: 'Applicants', icon: ApplicantsIcon },
    { to: '/facilities/countries', label: 'Facilities', icon: BusinessIcon },
    { to: '/markets', label: 'Markets', icon: LocationOnIcon },
    { to: '/pricing', label: 'Pricing', icon: AttachMoneyIcon },
    { to: '/sessions', label: 'Sessions', icon: EventIcon },
    { to: '/financials', label: 'Financials', icon: BarChartIcon },
    { to: '/contentfeed', label: 'Content Feed', icon: VideoLibraryIcon },
    { to: '/marketing', label: 'Marketing', icon: StoreIcon },
];

export const CITY_MANAGER_TITLES = ['City Manager I', 'City Manager II', 'City Manager III'];

export const isNavItemVisible = (item, { isCityManager, pricingAccess }) => {
    if (item.hideIfCityManager && isCityManager) return false;
    if (item.requiresPricingRole && pricingAccess !== 'allowed') return false;
    return true;
};

export const isNavItemActive = (pathname, to) => {
    const p = (pathname || '').toLowerCase();
    const t = to.toLowerCase();
    return p === t || p.startsWith(t + '/');
};

const css = `
.u-side{width:${SIDEBAR_WIDTH}px;flex:none;height:100vh;overflow-y:auto;overflow-x:hidden;background:var(--side);border-right:1px solid var(--line);padding:12px 12px 24px;scrollbar-width:thin;display:flex;flex-direction:column;transition:width .2s ease,padding .2s ease}
.u-side-top{display:flex;justify-content:flex-end;height:32px;margin-bottom:4px}
.u-collapse{width:32px;height:32px;border-radius:8px;border:0;background:transparent;display:inline-grid;place-items:center;cursor:pointer;color:var(--muted);transition:background .2s,color .2s}
.u-collapse:hover{background:var(--hover);color:var(--ink)}
.u-collapse:focus-visible{outline:2px solid var(--accent)!important;outline-offset:2px}
.u-collapse svg{font-size:22px}
.u-logo{display:flex;align-items:center;justify-content:center;height:44px;padding:0 12px;margin-bottom:18px;text-decoration:none}
.u-logo img{height:30px;width:auto;display:block}
html[data-theme="light"] .u-logo img{filter:brightness(0);opacity:.88}
.u-group{margin:0 0 22px}
.u-group .eb{display:block;padding:0 14px 8px;font:500 11px/1.3 ${FONT_MONO};letter-spacing:.12em;text-transform:uppercase;color:var(--muted)}
.u-nav a,.u-nav button{display:flex;align-items:center;gap:12px;width:100%;height:40px;padding:0 12px 0 14px;border:0;border-radius:8px;background:transparent;color:var(--muted);text-decoration:none;position:relative;font:400 14.5px/1 inherit;font-family:inherit;cursor:pointer;text-align:left;white-space:nowrap;transition:color .2s,background .2s}
.u-nav a:hover,.u-nav button:hover{color:var(--ink);background:var(--hover)}
.u-nav a.on{color:var(--ink);background:var(--accent-tint);font-weight:500}
.u-nav a.on::before{content:'';position:absolute;left:0;top:9px;bottom:9px;width:3px;border-radius:0 2px 2px 0;background:var(--accent)}
.u-nav .ic{flex:none;width:20px;height:20px;display:inline-grid;place-items:center;color:var(--muted);font-size:20px;line-height:1}
.u-nav .ic svg{font-size:20px}
.u-nav .ic.emoji{font-size:16px}
.u-nav a.on .ic{color:var(--accent)}
.u-nav .lbl{overflow:hidden;text-overflow:ellipsis}
.u-nav .new{margin-left:auto;font:500 10px/1 ${FONT_MONO};letter-spacing:.12em;color:var(--accent-text)}
.u-nav .cnt{margin-left:auto;font:500 11px/1 ${FONT_MONO};color:var(--muted);font-variant-numeric:tabular-nums}
.u-side .u-foot-nav{margin-top:auto;padding-top:6px;border-top:1px solid var(--line)}
.u-side.collapsed{width:${SIDEBAR_COLLAPSED_WIDTH}px;padding-left:10px;padding-right:10px}
.u-side.collapsed .u-side-top{justify-content:center}
.u-side.collapsed .u-group{margin-bottom:14px;padding-bottom:12px;border-bottom:1px solid var(--line)}
.u-side.collapsed .u-nav a,.u-side.collapsed .u-nav button{justify-content:center;padding:0;gap:0}
@media (prefers-reduced-motion:reduce){.u-side{transition:none}}
`;

function NavIcon({ icon }) {
    if (typeof icon === 'string') {
        return <span className="ic emoji" aria-hidden="true" data-icon="emoji">{icon}</span>;
    }
    const Icon = icon;
    return <span className="ic" aria-hidden="true"><Icon fontSize="inherit" /></span>;
}

// Collapsed rows show the icon only; the label (with its count) is in a tooltip and the link's name.
function WithTip({ collapsed, title, children }) {
    if (!collapsed) return children;
    return <Tooltip title={title} placement="right" arrow disableInteractive>{children}</Tooltip>;
}

const Sidebar = ({ handleLogout, onNavigate, collapsed = false, onToggleCollapse, id = 'panel-sidebar', className = '' }) => {
    const [totalLeads, setTotalLeads] = useState(0);
    const [totalPreferences, setTotalPreferences] = useState(0);
    const { employee } = useEmployee() || {};
    // T6-PRICING-PANEL-01: the Pricing tab shows only once mgmt-backend's gate lets this staff member through.
    const pricingAccess = usePricingAccess();
    const location = useLocation();

    const isCityManager = CITY_MANAGER_TITLES.includes(employee?.title);

    useEffect(() => {
        const fetchTotalPreferences = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preferences/count`);
                setTotalPreferences(response.data.count);
            } catch (error) {
                console.error("❌ Error fetching preferences:", error);
            }
        };
        fetchTotalPreferences();
    }, []);

    useEffect(() => {
        const fetchTotalLeads = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leads`);
                setTotalLeads(response.data.length);
            } catch (error) {
                console.error("❌ Error fetching leads:", error);
            }
        };
        fetchTotalLeads();
    }, []);

    const counts = {
        // Same numbers as before (the +200 is PD1 dummy item; B decides it separately).
        leads: totalLeads > 0 ? totalLeads + 200 : 0,
        preferences: (totalPreferences || 0).toLocaleString(),
    };

    const renderNavItem = (item) => {
        if (!isNavItemVisible(item, { isCityManager, pricingAccess })) return null;
        const active = isNavItemActive(location.pathname, item.to);
        const tip = item.countKey ? `${item.label} (${counts[item.countKey]})` : item.label;
        return (
            <WithTip key={item.to} collapsed={collapsed} title={tip}>
                <Link
                    to={item.to}
                    className={active ? 'on' : undefined}
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? tip : undefined}
                    onClick={onNavigate}
                    data-nav-item={item.label}
                >
                    <NavIcon icon={item.icon} />
                    {!collapsed && <span className="lbl">{item.label}</span>}
                    {!collapsed && item.badge && <span className="new">{item.badge}</span>}
                    {!collapsed && item.countKey && <span className="cnt" data-count={item.countKey}>{counts[item.countKey]}</span>}
                </Link>
            </WithTip>
        );
    };

    const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

    return (
        <aside
            id={id}
            className={`u-side${collapsed ? ' collapsed' : ''} ${className}`}
            aria-label="Main"
            data-shell-sidebar
            data-collapsed={collapsed ? 'true' : 'false'}
        >
            <style>{css}</style>
            {onToggleCollapse && (
                <div className="u-side-top">
                    <button
                        type="button"
                        className="u-collapse"
                        onClick={onToggleCollapse}
                        aria-label={toggleLabel}
                        title={toggleLabel}
                        aria-expanded={!collapsed}
                        aria-controls={id}
                        data-shell-collapse
                    >
                        {collapsed ? <ChevronRightIcon fontSize="inherit" /> : <ChevronLeftIcon fontSize="inherit" />}
                    </button>
                </div>
            )}

            {/* Logo: shown only when expanded, as before (7d83b73 Sidebar.js:237-245) */}
            {!collapsed && (
                <div className="u-logo" data-shell-logo>
                    <img src={LOGO_SRC} alt="United" />
                </div>
            )}

            <div className="u-group" data-nav-group="Enterprise AI">
                {!collapsed && <span className="eb">Enterprise AI</span>}
                <nav className="u-nav">{ENTERPRISE_AI_ITEMS.map(renderNavItem)}</nav>
            </div>

            <div className="u-group" data-nav-group="Workspace">
                {!collapsed && <span className="eb">Workspace</span>}
                <nav className="u-nav">{WORKSPACE_ITEMS.map(renderNavItem)}</nav>
            </div>

            <nav className="u-nav u-foot-nav">
                <WithTip collapsed={collapsed} title="Logout">
                    <button type="button" onClick={handleLogout} data-nav-item="Logout" aria-label={collapsed ? 'Logout' : undefined}>
                        <NavIcon icon={ExitToAppIcon} />
                        {!collapsed && <span className="lbl">Logout</span>}
                    </button>
                </WithTip>
            </nav>
        </aside>
    );
};

export default Sidebar;
